import { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faLink } from '@fortawesome/pro-solid-svg-icons';

import { copyToClipboard } from '@modules/helpers';

import { Box, Inline, Overlay, Stack, Text } from '@ui-lib';

import { BlinqLogoSvg } from '@components/BlinqLogo';

import { overlayStyleOverrides } from '../shared.css';

import {
  containerStyle,
  logoStyle,
  imageStyle,
  dashboardLinkButtonStyle,
  fadeDownAnimationStyle,
  checkMarkStyle,
  headingSizeOverride,
} from './styles.css';
import { useRouter } from 'next/router';
import { analytics } from '@modules/analytics';

const DASHBOARD_LINK = 'https://dash.blinq.me/';

const DashboardLinkButton = ({ isFromOnboarding }: { isFromOnboarding: boolean }) => {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async () => {
    if (isFromOnboarding) {
      analytics.track({
        name: 'Onboarding_Redirect_Link_Copied',
      });
    }
    try {
      await copyToClipboard(DASHBOARD_LINK);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      component="button"
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      paddingY="medium"
      paddingX="medium"
      boxShadow="borderNeutral"
      borderRadius="large"
      background={copied ? 'positiveLight' : 'body'}
      className={dashboardLinkButtonStyle}
      onClick={handleCopy}
    >
      <Inline alignY="center" nowrap gap="small">
        <FontAwesomeIcon size="lg" icon={faLink} />
        <Text>{DASHBOARD_LINK}</Text>
      </Inline>
      {copied ? (
        <Inline alignY="center" gap="xsmall">
          <FontAwesomeIcon icon={faCheck} className={copied ? checkMarkStyle : undefined} />
          <Text tone="positive" size="small">
            Copied!
          </Text>
        </Inline>
      ) : (
        <FontAwesomeIcon icon={faCopy} size="lg" />
      )}
    </Box>
  );
};

export const TeamAdminUnsupportedBreakpointOverlay = () => {
  const router = useRouter();
  const queryParams = router.query;
  const { from_onboarding } = queryParams;
  const isFromOnboarding = from_onboarding === 'true';
  const title = isFromOnboarding ? 'You’re all set up!' : 'Continue via desktop browser';
  const message =
    from_onboarding === 'true'
      ? 'Unlock full access via desktop browser'
      : 'The admin dashboard is only available on desktop';

  return (
    <Overlay className={overlayStyleOverrides}>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        paddingX="medium"
        className={containerStyle}
      >
        <Stack gap="large" align="center">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap={{
              mobile: 'medium',
              tablet: 'large',
            }}
            className={fadeDownAnimationStyle}
          >
            <BlinqLogoSvg className={logoStyle} />
            <Box component="h1" textAlign="center" className={headingSizeOverride}>
              {title}
            </Box>
            <Text tone="secondary" align="center">
              {message}
            </Text>
          </Box>
          <DashboardLinkButton isFromOnboarding={isFromOnboarding} />
          <Box component="img" src="/img/mbp.webp" className={imageStyle} />
        </Stack>
      </Box>
    </Overlay>
  );
};
